import type { VariantInput, PowerAnalysisResult, PowerProjection, ExperimentRecommendation } from './types';
import { normalCDF, normalQuantile } from './distributions';

/**
 * Fallback effect size, used only when the measured difference is too small to
 * size against: a near-zero effect needs an unbounded sample.
 */
const TARGET_RELATIVE_LIFT = 0.05;

/**
 * Below this relative difference the observed effect is treated as no signal.
 * Sizing for a 0.1% relative difference produces a sample size in the billions,
 * which is not a useful answer.
 */
const MIN_SIZEABLE_RELATIVE_EFFECT = 0.01;

/** Past this, a projection is noise rather than a forecast. */
const PROJECTION_HORIZON_DAYS = 365;

/**
 * Calculate the statistical power of the current experiment.
 * Power = probability of detecting a real effect given the current sample size.
 */
export function calculatePower(control: VariantInput, variant: VariantInput, alpha = 0.05): number {
  const p1 = control.sessions > 0 ? control.conversions / control.sessions : 0;
  const p2 = variant.sessions > 0 ? variant.conversions / variant.sessions : 0;

  if (p1 === 0 && p2 === 0) return 0;
  if (control.sessions === 0 || variant.sessions === 0) return 0;

  const effectSize = Math.abs(p2 - p1);
  if (effectSize === 0) return alpha;

  const pooledSE = Math.sqrt((p1 * (1 - p1)) / control.sessions + (p2 * (1 - p2)) / variant.sessions);
  if (pooledSE === 0) return 0;

  const zAlpha = normalQuantile(1 - alpha / 2);
  const zStat = effectSize / pooledSE;
  const power = normalCDF(zStat - zAlpha) + normalCDF(-zStat - zAlpha);

  return Math.round(Math.min(1, Math.max(0, power)) * 10000) / 10000;
}

/**
 * Smallest effect this sample size can reliably detect, at the target power.
 *
 * Defined as the exact inverse of calculateRequiredSampleSize rather than by its own
 * formula. The two used different variance assumptions, this one took
 * 2·p₁(1−p₁), treating both arms as having the control's variance, while the sample
 * size used p₁(1−p₁) + p₂(1−p₂), which accounts for the higher variance at the
 * larger rate. So they were not inverses: sizing for a 2.00pt effect at a 10%
 * control rate returned an n whose MDE then read 1.92pt, understating the floor by
 * about 4%.
 *
 * That matters because the power panel shows both, and the resolvable-effect band
 * compares the measured difference against this floor while the sample-size figure
 * is derived from the other. Solving the sizing equation makes the two agree by
 * construction: required(mde) is monotonically decreasing in mde, so bisection finds
 * the smallest effect whose required sample size the experiment has already reached.
 */
export function calculateMDE(controlRate: number, sessionsPerVariant: number, alpha = 0.05, targetPower = 0.8): number {
  if (sessionsPerVariant <= 0 || controlRate <= 0 || controlRate >= 1) return 0;

  let lo = 0;
  let hi = 1 - controlRate;

  // Even the largest possible effect needs more traffic than this: nothing is
  // detectable yet, and reporting a finite floor would overstate what is known.
  if (calculateRequiredSampleSize(controlRate, hi, alpha, targetPower) > sessionsPerVariant) {
    return Math.round(hi * 10000) / 10000;
  }

  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    if (mid <= 0) break;
    if (calculateRequiredSampleSize(controlRate, mid, alpha, targetPower) > sessionsPerVariant) {
      lo = mid;
    } else {
      hi = mid;
    }
  }

  return Math.round(Math.max(0, hi) * 10000) / 10000;
}

/**
 * Calculate required sample size per variant to detect a given effect.
 */
export function calculateRequiredSampleSize(controlRate: number, mde: number, alpha = 0.05, targetPower = 0.8): number {
  if (mde <= 0 || controlRate <= 0 || controlRate >= 1) return 0;

  const zAlpha = normalQuantile(1 - alpha / 2);
  const zBeta = normalQuantile(targetPower);

  const p2 = Math.min(controlRate + mde, 1);

  const n = (Math.pow(zAlpha + zBeta, 2) * (controlRate * (1 - controlRate) + p2 * (1 - p2))) / (mde * mde);

  return Math.ceil(n);
}

/**
 * Estimate days remaining to reach target power, based on current accumulation rate.
 */
type ProjectionInput = {
  requiredSessions: number;
  currentSessions: number;
  startMs: number;
  /** End of the run, or null while still running. */
  endMs: number | null;
  nowMs?: number;
};

/**
 * Days until the required sample size is reached.
 *
 * The traffic rate must be measured over the window the experiment was actually
 * live. Dividing by the time since it started counts the days since it *ended*
 * as if no traffic arrived, which understates the rate and inflates the estimate
 * a little further every day the page is opened.
 */
export function projectRemaining({
  requiredSessions,
  currentSessions,
  startMs,
  endMs,
  nowMs = Date.now(),
}: ProjectionInput): PowerProjection {
  if (requiredSessions > 0 && currentSessions >= requiredSessions) return { state: 'reached' };
  if (endMs !== null) return { state: 'finished' };
  if (currentSessions === 0 || !startMs) return { state: 'unknown' };

  const daysLive = (nowMs - startMs) / (1000 * 60 * 60 * 24);
  if (daysLive < 1) return { state: 'unknown' };

  const dailyRate = currentSessions / daysLive;
  if (dailyRate <= 0) return { state: 'unknown' };

  const days = Math.ceil((requiredSessions - currentSessions) / dailyRate);
  if (!Number.isFinite(days) || days <= 0) return { state: 'reached' };
  return days > PROJECTION_HORIZON_DAYS ? { state: 'unreachable', days } : { state: 'estimated', days };
}

/** @deprecated use projectRemaining: kept for the older call shape. */
export function estimateDaysRemaining(
  requiredSessions: number,
  currentSessions: number,
  experimentStartMs: number,
): number | null {
  const projection = projectRemaining({
    requiredSessions,
    currentSessions,
    startMs: experimentStartMs,
    endMs: null,
  });
  if (projection.state === 'reached') return 0;
  if (projection.state === 'estimated' || projection.state === 'unreachable') return projection.days;
  return null;
}

/**
 * Full power analysis for an experiment.
 */
type AnalyzePowerRun = {
  startsAt: number;
  /** End of the run, or null while still running. */
  endsAt: number | null;
  nowMs?: number;
};

export function analyzePower(
  control: VariantInput,
  variants: VariantInput[],
  run: number | AnalyzePowerRun,
): PowerAnalysisResult {
  const { startsAt, endsAt, nowMs } = typeof run === 'number' ? { startsAt: run, endsAt: null, nowMs: undefined } : run;
  const controlRate = control.sessions > 0 ? control.conversions / control.sessions : 0;

  // Use the variant with the largest effect for power calculation
  const powers = variants.map((v) => calculatePower(control, v));
  const maxPower = Math.max(...powers, 0);

  const avgSessionsPerVariant = Math.round(
    (control.sessions + variants.reduce((sum, v) => sum + v.sessions, 0)) / (1 + variants.length),
  );

  const mde = calculateMDE(controlRate, avgSessionsPerVariant);

  // Size against the difference actually being measured, not a fixed guess.
  //
  // requiredSampleSize inverts the same significance test the results table
  // reports: the n at which a difference of this size clears alpha = 0.05 with
  // 80% power. Sizing it for a hardcoded 5% lift answered "when will I be able
  // to detect 5%", which is not the question being asked of a running
  // experiment, that question is "when will the effect I am seeing become
  // conclusive".
  //
  // The observed effect must not be fed back through mde, which is itself
  // derived from the current n: that makes the answer self-fulfilling and
  // reports "sufficient data" for an underpowered experiment.
  const largest = variants.reduce<{ effect: number } | null>((best, v) => {
    const rate = v.sessions > 0 ? v.conversions / v.sessions : 0;
    const effect = Math.abs(rate - controlRate);
    return !best || effect > best.effect ? { effect } : best;
  }, null);

  const observedEffect = largest?.effect ?? 0;
  const floor = controlRate * MIN_SIZEABLE_RELATIVE_EFFECT;
  const sizedFor: 'observed' | 'target' = observedEffect > floor ? 'observed' : 'target';
  const effectToDetect = sizedFor === 'observed' ? observedEffect : controlRate * TARGET_RELATIVE_LIFT;

  const requiredSampleSize = calculateRequiredSampleSize(controlRate, effectToDetect);

  const projection = projectRemaining({
    requiredSessions: requiredSampleSize,
    currentSessions: avgSessionsPerVariant,
    startMs: startsAt,
    endMs: endsAt,
    nowMs,
  });

  return {
    power: maxPower,
    mde,
    requiredSampleSize,
    observedEffect,
    controlRate,
    // mde is the floor of what this sample size can detect. If the measured
    // difference sits below it, no amount of staring at the p-value will help.
    resolvable: observedEffect > 0 && mde > 0 && observedEffect >= mde,
    sizedFor,
    estimatedDaysRemaining:
      projection.state === 'reached'
        ? 0
        : projection.state === 'estimated' || projection.state === 'unreachable'
          ? projection.days
          : null,
    projection,
  };
}

/**
 * Generate a recommendation based on statistical results.
 */
export function generateRecommendation(
  control: VariantInput,
  variants: VariantInput[],
  significanceResults: { id: number; name: string; significant: boolean; pValue: number; improvement: number }[],
  bayesianResults: { id: number; probabilityOfBeingBest: number; expectedLoss: number }[],
  power: number,
): ExperimentRecommendation {
  const totalSessions = control.sessions + variants.reduce((sum, v) => sum + v.sessions, 0);

  if (totalSessions < 100) {
    return {
      winner: null,
      winnerVariantId: null,
      confidence: 'insufficient',
      message: 'Not enough data collected yet. Continue running the experiment.',
    };
  }

  // Check Bayesian results first (more intuitive)
  const bestBayesian = bayesianResults.reduce(
    (best, r) => (r.probabilityOfBeingBest > best.probabilityOfBeingBest ? r : best),
    bayesianResults[0]!,
  );

  const bestVariant = variants.find((v) => v.id === bestBayesian?.id);
  const bestSignificance = significanceResults.find((s) => s.id === bestBayesian?.id);

  if (!bestBayesian || !bestVariant || !bestSignificance) {
    return {
      winner: null,
      winnerVariantId: null,
      confidence: 'insufficient',
      message: 'Unable to determine a recommendation. Check experiment data.',
    };
  }

  if (bestBayesian.probabilityOfBeingBest >= 0.95 && bestSignificance.significant) {
    // improvement is a ratio (0.05 = +5%), so scale to percent before rounding.
    const liftPct = Math.round(bestSignificance.improvement * 100 * 100) / 100;
    return {
      winner: bestVariant.name,
      winnerVariantId: bestVariant.id,
      confidence: 'high',
      message: `${bestVariant.name} is the winner with ${Math.round(bestBayesian.probabilityOfBeingBest * 100)}% probability. Expected lift: ${liftPct > 0 ? '+' : ''}${liftPct}%.`,
    };
  }

  if (bestBayesian.probabilityOfBeingBest >= 0.8) {
    return {
      winner: bestVariant.name,
      winnerVariantId: bestVariant.id,
      confidence: 'medium',
      message: `${bestVariant.name} is trending as the winner (${Math.round(bestBayesian.probabilityOfBeingBest * 100)}% probability). Consider running longer for higher confidence.`,
    };
  }

  if (power < 0.5) {
    return {
      winner: null,
      winnerVariantId: null,
      confidence: 'low',
      message: `Statistical power is low (${Math.round(power * 100)}%). The experiment needs more data to detect meaningful differences.`,
    };
  }

  return {
    winner: null,
    winnerVariantId: null,
    confidence: 'low',
    message: 'No clear winner yet. Continue running the experiment to gather more data.',
  };
}
